// Tremor Raw Badge [v0.0.0]
import { type VariantProps } from "tailwind-variants";

import React from "react";
import { IconType } from "react-icons";

import { tv } from "@/lib/tailwind-utils";
import { cx } from "@/lib/tailwind-utils";

const badgeVariants = tv({
  base: cx(
    "inline-flex items-center gap-x-1 whitespace-nowrap rounded-md font-medium ring-1 ring-primary w-min",
  ),
  variants: {
    size: {
      xs: "px-1.5 py-0.5 text-tiny gap-x-0.5",
      sm: "px-2 py-1 text-caption",
      md: "px-2.5 py-1.5 text-body",
      lg: "px-3 py-2 text-body-lg",
    },
    variant: {
      default: [
        "bg-background-secondary text-foreground ring-border/10",
        "dark:bg-background-secondary dark:text-foreground dark:ring-border/20",
      ],
      neutral: [
        "bg-gray-100 text-gray-800 ring-gray-300/50",
        "dark:bg-gray-600 dark:text-gray-100 dark:ring-gray-600/50",
      ],
      success: [
        "bg-emerald-50 text-emerald-600 ring-emerald-600/10",
        "dark:bg-emerald-900/40 dark:text-emerald-400 dark:ring-emerald-400/20",
      ],
      error: [
        "bg-red-50 text-red-900 ring-red-600/10",
        "dark:bg-red-900/40 dark:text-red-400 dark:ring-red-400/20",
      ],
      warning: [
        "bg-yellow-50 text-yellow-900 ring-yellow-600/10",
        "dark:bg-yellow-900/40 dark:text-yellow-400 dark:ring-yellow-400/20",
      ],
      critical: ["bg-destructive/10 text-destructive ring-destructive/20"],
      info: [
        "bg-status-info-bg text-status-info-text ring-status-info-text/20",
      ],
      dark: [
        "bg-gray-700 text-white ring-gray-700/10",
        "dark:bg-gray-600 dark:text-gray-100 dark:ring-gray-500/20",
      ],
    },
  },
  defaultVariants: {
    variant: "default",
    size: "sm",
  },
});

interface BadgeProps
  extends
    React.ComponentPropsWithoutRef<"span">,
    VariantProps<typeof badgeVariants> {
  icon?: IconType;
}

const iconSizeMap = {
  xs: { size: 10, className: "h-2 w-2" },
  sm: { size: 14, className: "h-2.5 w-2.5" },
  md: { size: 16, className: "h-3 w-3" },
  lg: { size: 18, className: "h-4 w-4" },
} as const;

const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant, size, icon: Icon, ...props }, forwardedRef) => {
    const iconConfig = size ? iconSizeMap[size] : iconSizeMap.sm;
    return (
      <span
        ref={forwardedRef}
        className={cx(
          badgeVariants({ variant, size }),
          "flex items-center justify-center whitespace-nowrap",
          className,
        )}
        {...props}
      >
        {Icon && (
          <span className="flex items-center">
            <Icon size={iconConfig.size} className={iconConfig.className} />
          </span>
        )}
        {props.children}
      </span>
    );
  },
);

Badge.displayName = "Badge";
export default Badge;

export { badgeVariants, type BadgeProps };
